import React, { useState, useRef, useEffect, useMemo } from 'react';
import Button from '../common/Button';
import { Modal, ModalContent, ModalCloseButton, EloChange, ConfirmationModal } from '../common';
import {
  DEFAULT_SCORING,
  validateGameScore,
  shouldShowWinQuickButton,
  applyWinQuickScore,
} from '../../utils/gameScoreRules';
import { handleMobileScoreFocus, markSkipMobileScoreClear, mobileScoreInputAttrs } from '../../utils/mobileScoreInput';
import { buildCorrectedEloPayload, buildSessionChainReplayPreview } from '../../utils/scoreCorrection';
import { submitCompetitiveScoreCorrection } from '../../services/scoreCorrectionService';
import { getGenderTextColor } from '../../utils/genderColors';

function teamPlayersFromGame(game, teamNumber, playersById) {
  return (game?.players_session_games || [])
    .filter((row) => row.team_number === teamNumber)
    .map((row) => playersById[row.player_id] || row.players)
    .filter(Boolean);
}

function playerLabel(player) {
  if (!player) return 'Unknown';
  const name = `${player.first_name || ''} ${player.last_name || ''}`.trim();
  return name || player.name || 'Unknown';
}

function teamShortLabel(players) {
  if (!players.length) return 'Team';
  if (players.length === 1) return playerLabel(players[0]);
  return players.map((player) => (player.first_name || playerLabel(player)).trim()).join(' & ');
}

function TeamRoster({ players, teamTone, isWinner }) {
  const toneClasses =
    teamTone === 1
      ? {
          card: 'bg-team1-light ring-team1',
          accent: 'border-team1 text-team1',
        }
      : {
          card: 'bg-team2-light ring-team2',
          accent: 'border-team2 text-team2',
        };

  return (
    <div
      className={`relative flex-1 rounded-xl p-4 ring-1 ${toneClasses.card} ${
        isWinner ? 'ring-2 ring-success/70 shadow-sm' : ''
      }`}
    >
      {isWinner ? (
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-success-dark">
          Winner
        </span>
      ) : null}
      <div className={`border-l-4 pl-3 ${toneClasses.accent}`}>
        <p className="text-xs font-medium uppercase tracking-wide opacity-80">Team {teamTone}</p>
        <p className="mt-0.5 text-base leading-snug">
          {players.length ? (
            players.map((player, index) => (
              <React.Fragment key={player.id || playerLabel(player)}>
                {index > 0 ? <span className="font-semibold text-text-muted"> & </span> : null}
                <span className={`font-semibold ${getGenderTextColor(player.gender)}`}>
                  {playerLabel(player)}
                </span>
              </React.Fragment>
            ))
          ) : (
            <span className="font-semibold text-text-muted">Unknown team</span>
          )}
        </p>
      </div>
    </div>
  );
}

/**
 * Host/admin correction of a competitive session game.
 */
const EditCompetitiveGameModal = ({
  game,
  playersById = {},
  scoringConfig = DEFAULT_SCORING,
  correctionMode = 'live',
  allSessionGames = [],
  onClose,
  onCorrected,
}) => {
  const [team1Score, setTeam1Score] = useState(game?.team_1_score?.toString() || '');
  const [team2Score, setTeam2Score] = useState(game?.team_2_score?.toString() || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [scoreError, setScoreError] = useState('');
  const [showImpactConfirm, setShowImpactConfirm] = useState(false);
  const team1ScoreInputRef = useRef(null);
  const team2ScoreInputRef = useRef(null);

  const { pointsTarget } = scoringConfig;
  const originalTeam1 = Number(game?.team_1_score);
  const originalTeam2 = Number(game?.team_2_score);
  const originalWinner = Number(originalTeam1) > Number(originalTeam2) ? 1 : 2;

  useEffect(() => {
    if (team1Score !== '' && team2Score !== '') {
      const validationError = validateGameScore(team1Score, team2Score, scoringConfig);
      setScoreError(validationError || '');
    } else {
      setScoreError('');
    }
  }, [team1Score, team2Score, scoringConfig]);

  const handleScoreChange = (team, value) => {
    if (value === '' || /^\d+$/.test(value)) {
      if (team === 'team1') {
        setTeam1Score(value);
        if (value.length === 2 && team2ScoreInputRef.current) {
          markSkipMobileScoreClear('team2');
          team2ScoreInputRef.current.focus();
        }
      } else {
        setTeam2Score(value);
      }
      setError(null);
      setScoreError('');
    }
  };

  const handleScoreIncrement = (team, delta) => {
    const currentScore = team === 'team1' ? team1Score : team2Score;
    const newScore = Math.max(0, Math.min(99, (parseInt(currentScore, 10) || 0) + delta));
    const newScoreStr = newScore === 0 ? '' : newScore.toString();
    if (team === 'team1') setTeam1Score(newScoreStr);
    else setTeam2Score(newScoreStr);
    setError(null);
    setScoreError('');
  };

  const handleFlipScore = () => {
    setTeam1Score(team2Score);
    setTeam2Score(team1Score);
    setError(null);
    setScoreError('');
  };

  const showWinQuickButtons = shouldShowWinQuickButton(team1Score, team2Score, pointsTarget);
  const handleWinButton = (team) =>
    applyWinQuickScore(team, pointsTarget, {
      setTeam1Score,
      setTeam2Score,
      team1InputRef: team1ScoreInputRef,
      team2InputRef: team2ScoreInputRef,
      onClearErrors: () => {
        setError(null);
        setScoreError('');
      },
    });

  const parsed1 = parseInt(team1Score, 10);
  const parsed2 = parseInt(team2Score, 10);
  const scoresReady = !Number.isNaN(parsed1) && !Number.isNaN(parsed2);
  const scoresChanged = scoresReady && (parsed1 !== originalTeam1 || parsed2 !== originalTeam2);
  const newWinner = scoresReady && parsed1 !== parsed2 ? (parsed1 > parsed2 ? 1 : 2) : null;
  const winnerFlips = newWinner != null && newWinner !== originalWinner;
  const canFlipScore = team1Score !== '' || team2Score !== '';

  const eloPreview = useMemo(() => {
    if (!game || !scoresReady || parsed1 === parsed2 || scoreError) {
      return { preview: [], isEloExempt: true };
    }
    return buildCorrectedEloPayload(game, { team1Score: parsed1, team2Score: parsed2 }, playersById);
  }, [game, scoresReady, parsed1, parsed2, scoreError, playersById]);

  const needsImpactConfirm = correctionMode === 'post_session' || correctionMode === 'live_chain';
  const chainPreview = useMemo(() => {
    if (!needsImpactConfirm || !game || !scoresReady || parsed1 === parsed2 || scoreError || !scoresChanged) {
      return {
        laterGamesCount: 0,
        playerCount: 0,
        remainingRatedGames: 0,
        isEloExempt: true,
      };
    }
    return buildSessionChainReplayPreview(
      game,
      { team1Score: parsed1, team2Score: parsed2 },
      allSessionGames,
      playersById
    );
  }, [
    needsImpactConfirm,
    game,
    scoresReady,
    parsed1,
    parsed2,
    scoreError,
    scoresChanged,
    allSessionGames,
    playersById,
  ]);

  const applyCorrection = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await submitCompetitiveScoreCorrection({
        game,
        team1Score: parsed1,
        team2Score: parsed2,
        playersById,
        allSessionGames,
        correctionMode,
      });
      if (!result.ok) {
        setShowImpactConfirm(false);
        setError(result.message || 'Failed to update the score.');
        return;
      }
      onCorrected?.(result);
      onClose();
    } catch (err) {
      console.error('Error correcting competitive score:', err);
      setShowImpactConfirm(false);
      setError(err.message || 'Failed to update the score. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!scoresReady || parsed1 < 0 || parsed2 < 0) {
      setError('Please enter valid scores');
      return;
    }
    if (parsed1 === parsed2) {
      setError('Scores cannot be equal');
      return;
    }
    if (!scoresChanged) {
      setError('Scores are unchanged');
      return;
    }

    const validationError = validateGameScore(team1Score, team2Score, scoringConfig);
    if (validationError) {
      setError(validationError);
      setScoreError(validationError);
      return;
    }

    if (needsImpactConfirm) {
      setShowImpactConfirm(true);
      return;
    }

    await applyCorrection();
  };

  const team1Players = teamPlayersFromGame(game, 1, playersById);
  const team2Players = teamPlayersFromGame(game, 2, playersById);
  const team1ShortLabel = teamShortLabel(team1Players);
  const team2ShortLabel = teamShortLabel(team2Players);

  const previewByPlayer = useMemo(() => {
    const map = {};
    (eloPreview.preview || []).forEach((row) => {
      map[row.player_id] = row;
    });
    return map;
  }, [eloPreview.preview]);

  return (
    <>
    <Modal
      isOpen={!!game}
      onClose={onClose}
      closeOnBackdropClick={!isSubmitting && !showImpactConfirm}
      closeOnEscape={!isSubmitting && !showImpactConfirm}
    >
      <ModalContent size="medium" className="overflow-y-auto">
        <div className="mb-6 flex items-start justify-between gap-4 pr-8">
          <div>
            <h3 className="text-xl font-semibold text-text-primary">Edit score</h3>
            <p className="mt-1 text-sm text-text-secondary">
              Recorded as{' '}
              <span className="font-semibold tabular-nums text-text-primary">
                {originalTeam1}–{originalTeam2}
              </span>
            </p>
          </div>
          <ModalCloseButton onClose={onClose} />
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col gap-4 md:flex-row">
            <TeamRoster players={team1Players} teamTone={1} isWinner={originalWinner === 1} />
            <TeamRoster players={team2Players} teamTone={2} isWinner={originalWinner === 2} />
          </div>

          <div className="w-full rounded-xl border border-border bg-gradient-to-r from-team1-light to-team2-light p-4">
            <p className="mb-3 text-center text-xs font-medium uppercase tracking-wide text-text-secondary">
              New score
            </p>
            <div className="flex items-center justify-between gap-4">
              <div className="flex flex-1 flex-col items-center gap-1.5">
                <span className="max-w-full truncate px-1 text-xs font-semibold text-team1" title={team1ShortLabel}>
                  {team1ShortLabel}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleScoreIncrement('team1', -1)}
                    className="hidden h-8 w-8 items-center justify-center rounded-md border border-team1 bg-team1-light text-lg font-bold text-team1 transition-colors hover:bg-team1/30 sm:flex"
                    aria-label={`Decrease ${team1ShortLabel} score`}
                  >
                    −
                  </button>
                  <input
                    ref={team1ScoreInputRef}
                    placeholder="0"
                    value={team1Score}
                    onChange={(e) => handleScoreChange('team1', e.target.value)}
                    onFocus={(e) => handleMobileScoreFocus(e, 'team1', () => setTeam1Score(''))}
                    maxLength={2}
                    className="h-10 w-16 rounded-md border-2 border-team1 bg-surface px-2 text-center text-xl font-bold text-team1 shadow-sm focus:border-team1 focus:outline-none focus:ring-2 focus:ring-team1"
                    {...mobileScoreInputAttrs}
                  />
                  <button
                    type="button"
                    onClick={() => handleScoreIncrement('team1', 1)}
                    className="hidden h-8 w-8 items-center justify-center rounded-md border border-team1 bg-team1-light text-lg font-bold text-team1 transition-colors hover:bg-team1/30 sm:flex"
                    aria-label={`Increase ${team1ShortLabel} score`}
                  >
                    +
                  </button>
                </div>
                {showWinQuickButtons ? (
                  <button
                    type="button"
                    onClick={() => handleWinButton('team1')}
                    className="rounded-md bg-team1 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-team1/80"
                    aria-label={`Set ${team1ShortLabel} score to ${pointsTarget}`}
                  >
                    Win ({pointsTarget})
                  </button>
                ) : null}
              </div>

              <div className="flex flex-col items-center gap-0.5">
                <div className="h-0.5 w-6 bg-border-medium" />
                <span className="text-sm font-bold text-text-secondary">VS</span>
                <div className="h-0.5 w-6 bg-border-medium" />
              </div>

              <div className="flex flex-1 flex-col items-center gap-1.5">
                <span className="max-w-full truncate px-1 text-xs font-semibold text-team2" title={team2ShortLabel}>
                  {team2ShortLabel}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => handleScoreIncrement('team2', -1)}
                    className="hidden h-8 w-8 items-center justify-center rounded-md border border-team2 bg-team2-light text-lg font-bold text-team2 transition-colors hover:bg-team2/30 sm:flex"
                    aria-label={`Decrease ${team2ShortLabel} score`}
                  >
                    −
                  </button>
                  <input
                    ref={team2ScoreInputRef}
                    placeholder="0"
                    value={team2Score}
                    onChange={(e) => handleScoreChange('team2', e.target.value)}
                    onFocus={(e) => handleMobileScoreFocus(e, 'team2', () => setTeam2Score(''))}
                    maxLength={2}
                    className="h-10 w-16 rounded-md border-2 border-team2 bg-surface px-2 text-center text-xl font-bold text-team2 shadow-sm focus:border-team2 focus:outline-none focus:ring-2 focus:ring-team2"
                    {...mobileScoreInputAttrs}
                  />
                  <button
                    type="button"
                    onClick={() => handleScoreIncrement('team2', 1)}
                    className="hidden h-8 w-8 items-center justify-center rounded-md border border-team2 bg-team2-light text-lg font-bold text-team2 transition-colors hover:bg-team2/30 sm:flex"
                    aria-label={`Increase ${team2ShortLabel} score`}
                  >
                    +
                  </button>
                </div>
                {showWinQuickButtons ? (
                  <button
                    type="button"
                    onClick={() => handleWinButton('team2')}
                    className="rounded-md bg-team2 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-team2/80"
                    aria-label={`Set ${team2ShortLabel} score to ${pointsTarget}`}
                  >
                    Win ({pointsTarget})
                  </button>
                ) : null}
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                size="small"
                disabled={!canFlipScore}
                onClick={handleFlipScore}
                title="Swap the scores between both teams"
                aria-label="Flip score between teams"
              >
                Flip score
              </Button>
            </div>
          </div>

          {needsImpactConfirm && scoresChanged && !scoreError ? (
            <p className="text-xs text-text-secondary">
              {correctionMode === 'live_chain'
                ? 'You’ll review how this change affects later games this session before it’s saved.'
                : 'You’ll review how this change affects later games and ratings before it’s saved.'}
            </p>
          ) : null}

          {winnerFlips ? (
            <p className="rounded-md border border-warning bg-warning-subtle px-3 py-2 text-sm text-warning-foreground">
              {eloPreview.isEloExempt
                ? 'This will change the winner. Ratings are not affected for this game.'
                : 'This will change the winner and update ratings for this game.'}
            </p>
          ) : null}

          {!eloPreview.isEloExempt && eloPreview.preview.length > 0 && scoresChanged && !scoreError ? (
            <div className="space-y-1 rounded-md border border-border-medium bg-surface-subtle px-3 py-2">
              <p className="text-xs font-medium text-text-secondary">Rating change preview</p>
              {[...team1Players, ...team2Players].map((player) => {
                const row = previewByPlayer[player.id];
                if (!row) return null;
                return (
                  <div key={player.id} className="flex items-center justify-between gap-2 text-sm">
                    <span className={`truncate ${getGenderTextColor(player.gender)}`}>{playerLabel(player)}</span>
                    <span className="flex shrink-0 items-center gap-2 tabular-nums">
                      <EloChange change={row.oldChange} showSign size="xs" />
                      <span className="text-text-muted">→</span>
                      <EloChange change={row.newChange} showSign size="xs" />
                    </span>
                  </div>
                );
              })}
            </div>
          ) : null}

          {scoreError ? (
            <div className="rounded border border-danger/30 bg-danger-light px-3 py-2 text-center text-sm text-danger-dark">
              {scoreError}
            </div>
          ) : null}

          {error ? (
            <div className="rounded-lg border border-danger/30 bg-danger-light p-3">
              <p className="text-sm text-danger-dark">{error}</p>
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !!scoreError || !scoresChanged}
            >
              {isSubmitting ? 'Updating…' : 'Update score'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
    <ConfirmationModal
      isOpen={showImpactConfirm}
      onClose={() => !isSubmitting && setShowImpactConfirm(false)}
      onConfirm={applyCorrection}
      title="Confirm score update"
      confirmText={isSubmitting ? 'Updating…' : 'Confirm update'}
      cancelText="Back"
      loading={isSubmitting}
      message={
        <div className="space-y-2">
          <p className="font-medium text-text-primary">
            Change this game from {originalTeam1}–{originalTeam2} to {parsed1}–{parsed2}
            {winnerFlips ? ', and flip the winner' : ''}.
          </p>
          {correctionMode === 'live_chain' ? (
            <p>Later games this session will be re-rated.</p>
          ) : null}
          {chainPreview.laterGamesCount > 0 ? (
            <>
              <p>
                Recalculate ELO for{' '}
                <span className="font-semibold text-text-primary">{chainPreview.laterGamesCount}</span>
                {chainPreview.remainingRatedGames
                  ? ` of ${chainPreview.remainingRatedGames} remaining`
                  : ''}{' '}
                later game{chainPreview.laterGamesCount === 1 ? '' : 's'} in this session.
              </p>
              <p>
                Update club ratings for{' '}
                <span className="font-semibold text-text-primary">{chainPreview.playerCount}</span> player
                {chainPreview.playerCount === 1 ? '' : 's'}.
              </p>
            </>
          ) : (
            <p>No later rated games need to be recalculated.</p>
          )}
          {winnerFlips ? (
            <p>Partnership win/loss records for this game’s teammates will also update.</p>
          ) : null}
        </div>
      }
    />
    </>
  );
};

export default EditCompetitiveGameModal;
